import React, { useMemo } from "react";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminMainText,
  AdminSupportingText,
  AdminMetadataText,
  AdminStatusBadge,
} from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader } from './AdminWidgetChrome';

const PaymentWidget = () => {
  // Sample payment data
  const paymentData = {
    currentPlan: "Pro Monthly",
    status: "Active",
    nextBilling: "Sep 01, 2025",
    lastPayment: "AUD 49.00",
    progress: 70
  };

  const { cycleStart, cycleEnd, progressPct, daysRemaining } = useMemo(() => {
    const next = new Date(paymentData.nextBilling);
    const end = new Date(next);
    const start = new Date(next);
    // Move one month back for cycle start
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);
    const now = new Date();
    const totalMs = Math.max(end - start, 1);
    const elapsedMs = Math.min(Math.max(now - start, 0), totalMs);
    const pct = Math.round((elapsedMs / totalMs) * 100);
    const remainingDays = Math.max(Math.ceil((end - now) / (1000 * 60 * 60 * 24)), 0);
    return { cycleStart: start, cycleEnd: end, progressPct: pct, daysRemaining: remainingDays };
  }, [paymentData.nextBilling]);

  const formatDate = (date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });

  // For display: fixed 70% progress as requested
  const displayPct = 70;
  const DAY_MS = 1000 * 60 * 60 * 24;
  const totalDaysInCycle = Math.max(Math.ceil((cycleEnd - cycleStart) / DAY_MS), 1);
  const daysLeftDisplay = Math.max(Math.ceil(((100 - displayPct) / 100) * totalDaysInCycle), 0);

  return (
    <DashboardMainContainer className="masonry-item w-[420px] h-[333px] flex flex-col">
      <AdminWidgetHeader title="Payment Overview" />
      
      <div className="flex-1 p-4 space-y-4">
        {/* Current Plan */}
        <div className="flex items-center justify-between">
          <div>
            <AdminMainText className="text-offblack block">Current Plan</AdminMainText>
            <AdminSupportingText className="text-offblack">{paymentData.currentPlan}</AdminSupportingText>
          </div>
          <div className="flex items-center gap-2">
            <AdminStatusBadge status="Active" label={paymentData.status} />
          </div>
        </div>

        {/* Next Billing */}
        <div>
          <AdminMainText className="text-offblack block">Next Billing Date</AdminMainText>
          <AdminSupportingText className="text-offblack">{paymentData.nextBilling}</AdminSupportingText>
        </div>

        {/* Last Payment */}
        <div>
          <AdminMainText className="text-offblack block">Last Payment Amount</AdminMainText>
          <AdminSupportingText className="text-offblack">{paymentData.lastPayment}</AdminSupportingText>
        </div>

        {/* Monthly cycle progress - styled like PendingTasksWidget */}
        <div className="space-y-2">
          {/* Progress bar */}
          <div className="relative h-3">
            {/* Track */}
            <div className="absolute inset-y-0 left-0 right-0 flex items-center">
              <div className="w-full h-2 bg-lightgray rounded-[10px]"></div>
            </div>
            {/* Fill */}
            <div className="absolute inset-y-0 left-0 right-0 flex items-center z-10">
              <div
                className="h-2 rounded-[10px] transition-all duration-300 bg-primaryblue"
                style={{ width: `${displayPct}%`}}
              ></div>
            </div>
          </div>
          {/* Icon and days-left below the bar, aligned to progress position */}
          <div className="relative h-12">
            <div
              className="absolute -translate-x-1/2 left-0 top-0 flex flex-col items-center"
              style={{ left: `calc(${displayPct}% )` }}
            >
              <svg className="w-10 h-8 text-offblack" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <AdminMetadataText className="mt-0 text-offblack block">
                {daysLeftDisplay} Days Left
              </AdminMetadataText>
            </div>
          </div>
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default PaymentWidget;
